const POSTER_MAX_WIDTH = 1280;
const POSTER_TIMEOUT_MS = 15_000;
/** Seek a little way in: the very first frame is often black (fade-ins, encoder warm-up). */
const POSTER_SEEK_SECONDS = 1;

const posterTime = (duration: number) =>
  Number.isFinite(duration) && duration > 0
    ? Math.min(POSTER_SEEK_SECONDS, duration / 4)
    : POSTER_SEEK_SECONDS;

const posterFileName = (videoName: string) => `${videoName.replace(/\.[^.]+$/, '')}_poster.jpg`;

/**
 * Grabs a JPEG frame from a local video file to use as its `<video poster>` thumbnail.
 * Resolves to `null` when the browser can't decode the file (e.g. HEVC on Chrome) or times out.
 */
export const captureVideoPoster = (file: File): Promise<File | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const finish = (poster: File | null) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(poster);
    };

    const timeoutId = window.setTimeout(() => finish(null), POSTER_TIMEOUT_MS);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => finish(null);
    video.onloadedmetadata = () => {
      video.currentTime = posterTime(video.duration);
    };
    video.onseeked = () => {
      const {videoWidth, videoHeight} = video;
      if (!videoWidth || !videoHeight) {
        finish(null);
        return;
      }
      const scale = Math.min(1, POSTER_MAX_WIDTH / videoWidth);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(videoWidth * scale);
      canvas.height = Math.round(videoHeight * scale);
      const context = canvas.getContext('2d');
      if (!context) {
        finish(null);
        return;
      }
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) =>
          finish(blob ? new File([blob], posterFileName(file.name), {type: 'image/jpeg'}) : null),
        'image/jpeg',
        0.85
      );
    };
    video.src = url;
  });
